import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { AboutVisual } from './FuturisticVisuals';

export default function About() {
  const pillars = [
    'Multidisciplinary team across finance, legal, tech, HR and operations',
    'Engagements rooted in evidence, governance and measurable outcomes',
    'Confidentiality-first culture trusted by ministries and global institutions',
    'Capacity transfer built into every project, not bolted on at the end',
  ];

  return (
    <section id="about" className="relative py-20 sm:py-24 lg:py-28 bg-[#0d2143] overflow-hidden">
      <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-[#e8b863]/15 to-transparent hidden lg:block" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="relative aspect-[4/5] max-w-[480px] mx-auto rounded-3xl overflow-hidden border border-[#e8b863]/25 bg-[#0a1b35] shadow-2xl shadow-black/40">
            <AboutVisual className="w-full h-full" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1b35]/55 via-transparent to-transparent pointer-events-none" />
          </div>

          <div className="absolute -bottom-6 -right-3 sm:right-6 lg:right-10 bg-gradient-to-br from-[#122a52] to-[#1a3a6b] border border-[#e8b863]/30 rounded-2xl p-5 sm:p-6 max-w-[250px] shadow-2xl shadow-black/50">
            <p className="font-serif text-[40px] sm:text-[44px] leading-none gold-gradient">28+</p>
            <p className="text-white/85 text-[12.5px] sm:text-[13px] mt-2 leading-snug">
              years of combined leadership experience across finance, law &amp; strategy
            </p>
          </div>

          <div className="absolute -top-4 -left-4 w-20 h-20 border-l-2 border-t-2 border-[#e8b863]/45 rounded-tl-3xl" />
        </div>

        <div className="order-1 lg:order-2">
          <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-5">
            About the firm
          </p>
          <h2 className="font-serif text-white font-medium text-[32px] sm:text-[40px] lg:text-[50px] leading-[1.1] tracking-tight">
            A professional consulting firm built for{' '}
            <span className="italic gold-gradient">institutions that cannot afford to get it wrong.</span>
          </h2>

          <p className="mt-6 sm:mt-7 text-white/78 text-[15.5px] lg:text-[17px] leading-relaxed">
            We deliver business, financial, compliance, institutional development,
            technology and advisory solutions to government institutions, private
            businesses, NGOs, development partners, and corporate clients — combining
            local insight with disciplined, evidence-based practice.
          </p>

          <ul className="mt-8 space-y-4">
            {pillars.map((p) => (
              <li key={p} className="flex items-start gap-3.5">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#e8b863]/18 border border-[#e8b863]/45 flex items-center justify-center mt-0.5">
                  <Check className="w-3 h-3 text-[#e8b863]" strokeWidth={3} />
                </span>
                <span className="text-white/82 text-[14.5px] lg:text-[15.5px] leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/about"
            className="group mt-9 inline-flex items-center gap-2 text-[#e8b863] font-semibold text-[14px] tracking-wide border-b border-[#e8b863]/45 pb-1 hover:border-[#e8b863] transition-colors"
          >
            Read More About Us
            <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
}
